import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState, } from 'react';
import styles from './Typeahead.module.scss';
import { Icon } from '../Icon';
export const Typeahead = ({ label, placeholder = 'Search...', value, suggestions, onSelect, onChange, onSearch, clearable, invalid, disabled, inputWidth, className, showTopOverride, valueTriggerLimit = 2, }) => {
    const generatedId = useId();
    const inputId = generatedId;
    const [isOpen, setIsOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [showSuggestionList, setShowSuggestionList] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
    const [showTop, setShowTop] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    useEffect(() => {
        setFilteredSuggestions(suggestions);
    }, [suggestions]);
    useEffect(() => {
        if (!showSuggestionList)
            return;
        if (typeof showTopOverride === 'boolean') {
            setShowTop(showTopOverride);
        }
        else if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setShowTop(window.innerHeight / 2 < rect.top);
        }
    }, [showSuggestionList, showTopOverride]);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!containerRef.current?.contains(e.target)) {
                setIsOpen(false);
                setShowSuggestionList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const runSearch = (input) => {
        if (onSearch) {
            onSearch(input);
        }
        else {
            const normalized = input.toLowerCase();
            const filtered = suggestions.filter((s) => (s.label || s.value).toLowerCase().includes(normalized));
            setFilteredSuggestions(filtered);
        }
        setShowSuggestionList(input.length >= valueTriggerLimit);
    };
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        runSearch(newValue);
        onChange({
            target: {
                name: inputId,
                value: newValue,
            },
        });
        setShowSuggestionList(newValue.length >= valueTriggerLimit);
        setHighlighted(-1);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setShowSuggestionList(false);
            e.currentTarget.blur();
            return;
        }
        if (!isOpen)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((prev) => Math.min(prev + 1, filteredSuggestions.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((prev) => Math.max(prev - 1, 0));
        }
        else if (e.key === 'Enter' && highlighted >= 0) {
            e.preventDefault();
            handleSelect(filteredSuggestions[highlighted]);
        }
    };
    const handleSelect = (s) => {
        if (s.disabled)
            return;
        onSelect(s);
        setIsOpen(false);
        setShowSuggestionList(false);
    };
    const handleClear = () => {
        if (onSearch) {
            onSearch('');
        }
        else {
            setFilteredSuggestions(suggestions);
        }
        onChange({
            target: {
                name: inputId,
                value: '',
            },
        });
        setIsOpen(false);
        setShowSuggestionList(false);
    };
    const handleToggleSuggestionList = () => {
        const next = !showSuggestionList;
        setShowSuggestionList(next);
        setIsOpen(next);
        if (next && (valueTriggerLimit === 0 || value.length >= valueTriggerLimit) && onSearch) {
            onSearch(value);
        }
        if (next)
            inputRef.current?.focus();
    };
    const handleFocus = () => {
        setIsFocused(true);
        if (valueTriggerLimit === 0 || value.length >= valueTriggerLimit) {
            setIsOpen(true);
            setShowSuggestionList(true);
            if (onSearch)
                onSearch(value);
        }
    };
    const handleBlur = () => {
        setIsFocused(false);
    };
    const isClearable = !!(clearable && value);
    const iconName = isClearable ? 'fas fa-times' : 'fas fa-chevron-down';
    const rootClasses = [
        styles.root,
        invalid ? styles.invalid : '',
        disabled ? styles.disabled : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const inputClasses = [
        styles.input,
        isFocused ? styles['input--focused'] : '',
        value ? styles['input--filled'] : '',
        disabled ? styles['input--disabled'] : '',
        invalid ? styles['input--error'] : '',
    ]
        .filter(Boolean)
        .join(' ');
    const iconClasses = [
        styles.icon,
        styles['icon--clickable'],
        !isClearable && showSuggestionList && filteredSuggestions.length > 0
            ? styles['icon--expanded']
            : '',
        invalid && !isFocused ? styles['icon--invalid'] : '',
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("div", { ref: containerRef, className: rootClasses, children: [label && (_jsx("label", { htmlFor: inputId, className: styles.label, children: label })), _jsxs("div", { className: styles.inputWrapper, style: inputWidth ? { width: inputWidth } : undefined, children: [_jsx("input", { ref: inputRef, id: inputId, type: "text", className: inputClasses, value: value, placeholder: placeholder, disabled: disabled, onChange: handleInputChange, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown, role: "combobox", "aria-expanded": isOpen, "aria-controls": `${inputId}-dropdown`, "aria-invalid": invalid }), _jsx("div", { className: iconClasses, role: "button", tabIndex: 0, onClick: () => {
                            if (disabled)
                                return;
                            if (isClearable) {
                                handleClear();
                            }
                            else if (clearable !== false) {
                                handleToggleSuggestionList();
                            }
                        }, "aria-label": isClearable ? 'Clear' : 'Toggle suggestions', children: _jsx(Icon, { faIconName: iconName }) })] }), showSuggestionList && filteredSuggestions.length > 0 && (_jsx("ul", { className: [styles.dropdown, showTop ? styles.top : ''].filter(Boolean).join(' '), id: `${inputId}-dropdown`, role: "listbox", children: filteredSuggestions.map((suggestion, i) => (_jsx("li", { className: [
                        styles.item,
                        i === highlighted ? styles.highlighted : '',
                        suggestion.disabled ? styles.itemDisabled : '',
                    ]
                        .filter(Boolean)
                        .join(' '), onMouseEnter: () => setHighlighted(i), onClick: () => handleSelect(suggestion), role: "option", "aria-selected": i === highlighted, "aria-disabled": suggestion.disabled, tabIndex: -1, children: suggestion.label }, suggestion.id))) }))] }));
};
